import React, {useState} from 'react';

import {assert} from 'Asserts';
import {HighImpactChange} from 'config-migration/MigrationConstants';
import {
  ChildPlansAndSubtractedObjects,
  PrimaryObjectPlan,
  SecondaryObjectPlan,
} from 'config-migration/types/MigrationObjects';
import {SecondaryObjectType} from 'config-migration/types/ObjectType';

import {
  ChildImportPlanRow,
  ChildImportPlanSectionWrapper,
  getSubtractedRowsFromPlan,
} from './ChildImportPlanSection';
import {NestedCardRowType} from './utils/childImportPlanUtils';
import {
  getChildSectionTitleText,
  getPlanForUpdatedChildPlan,
  getSortedChildMigrationPlans,
} from './utils/childImportPlanUtils';

export interface NestedChildImportPlanRowProps {
  plan: PrimaryObjectPlan;
  childPlan: SecondaryObjectPlan;
  childOriginalIndex: number | undefined;
  nestedDepth: number;
  onUpdatePlan: (plan: PrimaryObjectPlan) => void;
  highImpactChangeMap: Map<string, Array<HighImpactChange>>;
}

export default function NestedChildImportPlanRow(props: NestedChildImportPlanRowProps): JSX.Element {
  const [isCollapsed, setIsCollapsed] = useState<boolean>(true);
  const relevantChildPlans = props.plan.childPlansByType.get(props.childPlan.objectType);
  assert(relevantChildPlans !== undefined, `No child plans found for ${props.childPlan.objectType}`);

  return (
    <>
      <ChildImportPlanRow
        key={props.childPlan.objectId}
        plan={props.childPlan}
        onUpdateChildPlan={(updatedChildPlan: SecondaryObjectPlan) => {
          props.onUpdatePlan(
            getPlanForUpdatedChildPlan(
              props.plan,
              relevantChildPlans,
              updatedChildPlan,
              props.childOriginalIndex
            ) as PrimaryObjectPlan
          );
        }}
        nestedDepth={props.nestedDepth}
        highImpactChanges={props.highImpactChangeMap.get(props.childPlan.objectId) || []}
        nestedCardRowType={
          props.childPlan.childPlansByType.size > 0
            ? NestedCardRowType.Header
            : NestedCardRowType.HeaderAndFooter
        }
        setIsCollapsed={setIsCollapsed}
        isCollapsed={isCollapsed}
        hasChildren={true}
      />
      {!isCollapsed &&
        Array.from(props.childPlan.childPlansByType.keys()).map((childType) => (
          <GrandchildImportPlanSection
            parentPlan={props.plan}
            childPlan={props.childPlan}
            grandchildType={childType}
            relevantChildPlans={relevantChildPlans}
            childOriginalIndex={props.childOriginalIndex}
            onUpdatePlan={props.onUpdatePlan}
            nestedDepth={props.nestedDepth}
            highImpactChangeMap={props.highImpactChangeMap}
            key={childType}
          />
        ))}
    </>
  );
}

interface GrandchildImportPlanSectionProps {
  parentPlan: PrimaryObjectPlan;
  childPlan: SecondaryObjectPlan;
  relevantChildPlans: ChildPlansAndSubtractedObjects;
  grandchildType: SecondaryObjectType;
  childOriginalIndex: number | undefined;
  nestedDepth: number;
  onUpdatePlan: (plan: PrimaryObjectPlan) => void;
  highImpactChangeMap: Map<string, Array<HighImpactChange>>;
}

function GrandchildImportPlanSection(props: GrandchildImportPlanSectionProps): JSX.Element {
  const relevantGrandchildPlans = props.childPlan.childPlansByType.get(props.grandchildType);
  assert(relevantGrandchildPlans !== undefined, `No child plans found for ${props.grandchildType}`);
  const sortedGrandchildPlans = getSortedChildMigrationPlans(relevantGrandchildPlans.childMigrationPlans);

  const originalIndexMap = new Map(
    relevantGrandchildPlans.childMigrationPlans.map((plan, index) => [plan.objectId, index])
  );
  return (
    <ChildImportPlanSectionWrapper
      title={getChildSectionTitleText(props.childPlan, props.grandchildType)}
      nestedDepth={props.nestedDepth}
      nestedCardRowType={NestedCardRowType.Header}
    >
      {sortedGrandchildPlans.map((grandchild, index) => (
        <ChildImportPlanRow
          key={grandchild.objectId}
          plan={grandchild}
          onUpdateChildPlan={(updatedGrandchildPlan: SecondaryObjectPlan) => {
            const updatedChildPlan = getPlanForUpdatedChildPlan(
              props.childPlan,
              relevantGrandchildPlans,
              updatedGrandchildPlan,
              originalIndexMap.get(grandchild.objectId)
            );
            props.onUpdatePlan(
              getPlanForUpdatedChildPlan(
                props.parentPlan,
                props.relevantChildPlans,
                updatedChildPlan as SecondaryObjectPlan,
                props.childOriginalIndex
              ) as PrimaryObjectPlan
            );
          }}
          nestedDepth={props.nestedDepth}
          highImpactChanges={props.highImpactChangeMap.get(grandchild.objectId) || []}
          nestedCardRowType={
            relevantGrandchildPlans.subtractedChildObjects.length === 0 &&
            index === sortedGrandchildPlans.length - 1
              ? NestedCardRowType.Footer
              : NestedCardRowType.Regular
          }
          setIsCollapsed={null}
          isCollapsed={false}
          hasChildren={false}
        />
      ))}
      {getSubtractedRowsFromPlan(
        props.childPlan,
        props.grandchildType,
        props.highImpactChangeMap,
        props.nestedDepth,
        true
      )}
    </ChildImportPlanSectionWrapper>
  );
}
